import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react'

import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'

const ForgotPassword = () => {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [resetCode, setResetCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [step, setStep] = useState(1) // 1: email, 2: code, 3: new password

  const handleSendResetCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1000))

      // Simulate sending reset code
      if (!email.trim()) {
        throw new Error('Please enter your email address')
      }

      setSuccess(true)
      setStep(2)
      console.log(`Reset code would be sent to: ${email}`)

      // For demo purposes, generate a mock code
      const mockCode = Math.floor(100000 + Math.random() * 900000).toString()
      console.log(`Mock reset code: ${mockCode}`)
      alert(`For demo: Your reset code is ${mockCode}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset code')
      console.error('Reset code error:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 800))

      // For demo, accept any 6-digit code
      if (!resetCode.trim() || resetCode.length !== 6) {
        throw new Error('Please enter a valid 6-digit code')
      }

      setStep(3)
      setSuccess(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid verification code')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!newPassword) {
      setError('Please enter a new password')
      return
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setIsLoading(true)

    try {
      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1000))

      // Simulate successful password reset
      alert('Password reset successful! You can now log in with your new password.')

      // Reset form
      setEmail('')
      setResetCode('')
      setNewPassword('')
      setConfirmPassword('')
      setStep(1)
      setSuccess(false)
    } catch (err) {
      setError('Failed to reset password. Please try again.')
      console.error('Reset password error:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendCode = async () => {
    setError('')
    setIsLoading(true)

    try {
      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1000))
      alert('Reset code has been resent to your email')
    } catch (err) {
      setError('Failed to resend code. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-secondary-900 dark:to-secondary-950 p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-primary-600 dark:text-primary-400" />
          </div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">
            Reset Password
          </h1>
          <p className="text-secondary-600 dark:text-secondary-400 mt-2">
            {step === 1 && 'Enter email to receive reset code'}
            {step === 2 && 'Enter verification code sent to your email'}
            {step === 3 && 'Set your new password'}
          </p>
        </div>

        <Card className="relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute top-0 left-0 w-32 h-32 bg-primary-100 dark:bg-primary-900/20 rounded-full -translate-x-16 -translate-y-16" />
          <div className="absolute bottom-0 right-0 w-40 h-40 bg-secondary-100 dark:bg-secondary-800/20 rounded-full translate-x-20 translate-y-20" />

          <div className="relative">
            {/* Back button */}
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                className="mb-6 flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
            )}

            {error && (
              <div className="mb-6 p-4 rounded-lg border border-error-200 bg-error-50 dark:border-error-800 dark:bg-error-900/20">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-error-600 dark:text-error-400" />
                  <p className="text-error-700 dark:text-error-300">{error}</p>
                </div>
              </div>
            )}

            {success && step < 3 && (
              <div className="mb-6 p-4 rounded-lg border border-success-200 bg-success-50 dark:border-success-800 dark:bg-success-900/20">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-success-600 dark:text-success-400" />
                  <p className="text-success-700 dark:text-success-300">
                    {step === 1 ? 'Reset code sent successfully!' : 'Code verified successfully!'}
                  </p>
                </div>
              </div>
            )}

            {/* Step 1: Email */}
            {step === 1 && (
              <form onSubmit={handleSendResetCode} className="space-y-6">
                <div className="space-y-4">
                  <Input
                    label="Email Address"
                    type="email"
                    placeholder="admin@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    leftIcon={<Mail className="w-5 h-5" />}
                  />
                </div>

                <div className="space-y-2">
                  <Button
                    type="submit"
                    variant="primary"
                    fullWidth
                    isLoading={isLoading}
                  >
                    {isLoading ? 'Sending...' : 'Send Reset Code'}
                  </Button>
                  <div className="text-sm text-secondary-500 dark:text-secondary-400 text-center">
                    We'll send a 6-digit code to your email
                  </div>
                </div>
              </form>
            )}

            {/* Step 2: Verification Code */}
            {step === 2 && (
              <form onSubmit={handleVerifyCode} className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-900 dark:text-white mb-2">
                      Verification Code
                    </label>
                    <input
                      type="text"
                      placeholder="123456"
                      maxLength={6}
                      value={resetCode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '')
                        if (value.length <= 6) {
                          setResetCode(value)
                        }
                      }}
                      className="w-full px-4 py-3 text-2xl font-bold tracking-widest text-center rounded-lg border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                      autoFocus
                    />
                    <div className="text-sm text-secondary-500 dark:text-secondary-400 text-center mt-2">
                      Enter the 6-digit code sent to {email}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <Button
                    type="submit"
                    variant="primary"
                    fullWidth
                    isLoading={isLoading}
                  >
                    {isLoading ? 'Verifying...' : 'Verify Code'}
                  </Button>
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={handleResendCode}
                      className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 disabled:text-secondary-400 disabled:cursor-not-allowed"
                      disabled={isLoading}
                    >
                      Resend code
                    </button>
                  </div>
                </div>
              </form>
            )}

            {/* Step 3: New Password */}
            {step === 3 && (
              <form onSubmit={handleResetPassword} className="space-y-6">
                <div className="space-y-4">
                  <Input
                    label="New Password"
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value)
                      if (e.target.value !== confirmPassword) {
                        setError('')
                      }
                    }}
                    required
                    minLength={8}
                  />
                  <Input
                    label="Confirm Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value)
                      if (newPassword !== e.target.value) {
                        setError('')
                      }
                    }}
                    required
                  />
                  <div className="flex items-center justify-between text-sm text-secondary-500 dark:text-secondary-400">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showNewPassword}
                        onChange={(e) => setShowNewPassword(e.target.checked)}
                        className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 border-secondary-300 dark:border-secondary-600"
                      />
                      Show new password
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showConfirmPassword}
                        onChange={(e) => setShowConfirmPassword(e.target.checked)}
                        className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 border-secondary-300 dark:border-secondary-600"
                      />
                      Show confirm password
                    </label>
                  </div>
                </div>

                <div className="space-y-2">
                  <Button
                    type="submit"
                    variant="primary"
                    fullWidth
                    isLoading={isLoading}
                  >
                    {isLoading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                  <div className="text-sm text-secondary-500 dark:text-secondary-400 text-center">
                    Your password must be at least 8 characters long
                  </div>
                </div>
              </form>
            )}

            <div className="mt-8 text-center">
              <p className="text-secondary-600 dark:text-secondary-400">
                Remember your password?{' '}
                <Link
                  to="/login"
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 font-medium transition-colors"
                >
                  Sign in
                </Link>
              </p>
            </div>

            {/* Password Tips */}
            <div className="mt-8 pt-6 border-t border-secondary-200 dark:border-secondary-700">
              <h4 className="font-medium text-secondary-900 dark:text-white mb-2">
                Password Security Tips:
              </h4>
              <ul className="space-y-1 text-sm text-secondary-600 dark:text-secondary-400">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-3 h-3 text-success-500" />
                  <span>Use at least 8 characters</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-3 h-3 text-success-500" />
                  <span>Include uppercase and lowercase letters</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-3 h-3 text-success-500" />
                  <span>Add numbers and special characters</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-3 h-3 text-success-500" />
                  <span>Avoid common words and personal info</span>
                </li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default ForgotPassword